import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../services/api';

/**
 * TenantContext
 * ─────────────
 * Responsible for:
 *   1. Detecting the tenantId from the browser hostname/subdomain.
 *   2. Fetching the tenant's branding config from the backend.
 *   3. Injecting CSS custom properties into <html> for dynamic theming.
 *   4. Providing tenant data to all child components.
 */

const TenantContext = createContext(null);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Extract tenantId from the browser's hostname.
 * Rules:
 *   - tenant1.example.com       → "tenant1"
 *   - tenant1.localhost          → "tenant1"
 *   - localhost                  → use REACT_APP_DEFAULT_TENANT or "demo"
 *   - 192.168.x.x               → use REACT_APP_DEFAULT_TENANT or "demo"
 */
const detectTenantId = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // IP address or plain localhost → fall back to env var or "demo"
  if (parts.length === 1 || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return process.env.REACT_APP_DEFAULT_TENANT || 'demo';
  }

  // Explicit override from env (useful for local dev)
  if (process.env.REACT_APP_TENANT_ID) {
    return process.env.REACT_APP_TENANT_ID;
  }

  // Subdomain is the first part — skip "www" and "api"
  const sub = parts[0].toLowerCase();
  if (sub === 'www' || sub === 'api') {
    return process.env.REACT_APP_DEFAULT_TENANT || 'demo';
  }

  return sub;
};

/**
 * Apply CSS custom properties to :root for dynamic theming.
 */
const applyTheme = (theme = {}) => {
  const root = document.documentElement.style;
  root.setProperty('--color-primary', theme.primaryColor || '#12206b');
  root.setProperty('--color-secondary', theme.secondaryColor || '#00b4d8');
  root.setProperty('--color-accent', theme.accentColor || '#e8f7fc');
  root.setProperty('--color-text', theme.textColor || '#1F2937');
  root.setProperty('--color-bg', theme.bgColor || '#FFFFFF');
  root.setProperty('--color-sidebar', theme.sidebarColor || '#12206b');
  root.setProperty('--color-sidebar-text', theme.sidebarTextColor || '#FFFFFF');
};

// ─── Default / fallback tenant config ────────────────────────────────────────
const DEFAULT_CONFIG = {
  tenantId: 'demo',
  appName: 'Tzar Help Desk',
  logoUrl: '/image.png',
  faviconUrl: '/image.png',
  supportPhone: '+91 98765 43210',
  ticketCategories: [
    // IT
    'Hardware Support', 'Networking', 'Software Issues', 'Account & Access', 'Email / VPN', 'Security', 'Infrastructure', 'Development',
    // HR
    'Leave Request', 'Payslip / Salary', 'Attendance', 'Onboarding', 'HR Policy',
    // Accounts
    'Invoice Query', 'Payment Issue', 'Reimbursement', 'GST / Tax', 'Purchase Order',
    // Admin
    'Office Facility', 'Travel Request', 'Stationery / Supplies', 'Visitor / Pass',
    'Other',
  ],
  kbCategories: [
    'Getting Started',
    // IT
    'Hardware', 'Networking', 'Software', 'Security', 'Account & Access', 'Development', 'Infrastructure',
    // HR
    'HR Policies', 'Leave & Attendance', 'Payroll',
    // Accounts
    'Billing & Invoicing', 'Reimbursements', 'Taxation',
    // Admin
    'Office & Facilities', 'Travel & Stationery',
    'Troubleshooting', 'Other',
  ],
  whiteLabelEnabled: false,
  theme: {
    primaryColor: '#12206b',
    secondaryColor: '#00b4d8',
    accentColor: '#e8f7fc',
    textColor: '#1F2937',
    bgColor: '#FFFFFF',
    sidebarColor: '#12206b',
    sidebarTextColor: '#FFFFFF',
  },
  labels: {
    supportPortal: 'IT Support Portal',
    ticketManagement: 'IT Requests',
    knowledgeBase: 'Knowledge Base',
    helpCenter: 'Help Center',
    submitTicket: 'Raise a Request',
    myTickets: 'My Requests',
    dashboard: 'Dashboard',
  },
  features: {
    knowledgeBase: true,
    liveChat: true,
    emailNotifications: true,
    feedbackRating: true,
    slaManagement: false,
    reportingAnalytics: true,
    multiLanguage: true,
  },
};

// ─── Provider ─────────────────────────────────────────────────────────────────
export const TenantProvider = ({ children }) => {
  const [tenantId] = useState(() => detectTenantId());
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTenantConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Pass X-Tenant-ID header so the backend can resolve regardless of proxy
      const { data } = await api.get('/api/tenant/config', {
        headers: { 'X-Tenant-ID': tenantId },
      });
      if (data.success && data.data) {
        setConfig(data.data);
        applyTheme(data.data.theme);
      }
    } catch (err) {
      console.warn('[TenantProvider] Could not load tenant config, using defaults.', err.message);
      setError(err.message);
      applyTheme(DEFAULT_CONFIG.theme);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadTenantConfig();
  }, [loadTenantConfig]);

  // Update page <title> and favicon when config changes
  useEffect(() => {
    document.title = config.appName || 'Tzar Help Desk';

    // Favicon
    const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
    link.rel = 'icon';
    link.href = config.faviconUrl || config.logoUrl || '/image.png';
    document.head.appendChild(link);
  }, [config]);

  const value = {
    tenantId,
    config,
    loading,
    error,
    reload: loadTenantConfig,
    // Shortcuts
    appName: config.appName,
    labels: config.labels,
    features: config.features,
    theme: config.theme,
    logoUrl: config.logoUrl,
    supportPhone: config.supportPhone || '',
    whiteLabelEnabled: config.whiteLabelEnabled ?? false,
    ticketCategories: config.ticketCategories?.length ? config.ticketCategories : ['Technical', 'Billing', 'General', 'Feature Request', 'Bug', 'Other'],
    kbCategories: config.kbCategories?.length ? config.kbCategories : ['General', 'Getting Started', 'Account', 'Billing', 'Technical', 'Troubleshooting'],
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within <TenantProvider>');
  return ctx;
};

export default TenantContext;
